import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Spinner, DiscordLogo, Warning } from "@phosphor-icons/react";
import { API, getVisitorId } from "@/lib/api";

export default function ReferralHandler() {
  const { code } = useParams();
  const [state, setState] = useState({ phase: "loading" }); // loading | oauth-off | error

  useEffect(() => {
    (async () => {
      try {
        // 1) Resolve link (validates code, gets creator meta)
        const linkRes = await axios.get(`${API}/links/resolve/${code}`);
        // 2) Record the click (fire-and-forget attribution)
        await axios.post(`${API}/clicks`, {
          code,
          visitor_id: getVisitorId(),
          referrer: document.referrer,
          user_agent: navigator.userAgent,
        }).catch(() => {});
        // 3) Check if Discord OAuth is configured
        const settings = await axios.get(`${API}/settings/public`);
        if (!settings.data.discord_oauth_enabled) {
          // Fallback: no OAuth — creator-specific invite > global invite
          const invite = linkRes.data.creator?.discord_invite_url || settings.data.discord_invite_url;
          if (invite) {
            window.location.replace(invite);
            return;
          }
          setState({ phase: "oauth-off", creator: linkRes.data.creator });
          return;
        }
        // 4) Kick off OAuth
        const authRes = await axios.get(`${API}/auth/discord/authorize`, {
          params: { kind: "visitor", code },
        });
        window.location.replace(authRes.data.url);
      } catch (e) {
        const d = e?.response?.data?.detail;
        setState({ phase: "error", msg: typeof d === "string" ? d : "Referral link not available" });
      }
    })();
  }, [code]);

  if (state.phase === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card-tactical p-8 max-w-md text-center">
          <Spinner size={28} className="animate-spin mx-auto" color="#00F0FF" />
          <div className="kbd-label text-neon-cyan mt-4">TRACKING · REDIRECTING TO DISCORD</div>
          <p className="text-white/50 text-xs mt-2 font-mono">If nothing happens in a few seconds, refresh.</p>
        </div>
      </div>
    );
  }

  if (state.phase === "oauth-off") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="card-tactical p-8 max-w-md">
          <Warning size={28} color="#E4FF00" />
          <h2 className="display-heading font-bold text-xl mt-3">Discord verification unavailable</h2>
          <p className="text-white/60 text-sm mt-2">
            The server owner hasn't finished wiring up Discord OAuth or the invite URL yet.
            Your click was recorded for <span className="text-neon-cyan font-mono">{state.creator?.display_name}</span>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="card-tactical p-8 max-w-md">
        <h2 className="display-heading font-bold text-xl">Link unavailable</h2>
        <p className="text-white/60 text-sm mt-2 font-mono">{state.msg}</p>
      </div>
    </div>
  );
}
